import { useState } from 'react';
import type { PiCatalogueModel } from '../../shared/api.ts';

/**
 * A Pi MODEL FIELD: the signed-in provider's catalogue when there is one, free
 * text when there is not.
 *
 * Pi's model dials are free text everywhere else in this drawer, and for a
 * reason that still holds on the metered branch — the endpoint is a setting, so
 * no list this studio ships could keep up with what an arbitrary provider
 * serves. A SUBSCRIPTION removes that reason and only that one: the CLI holds
 * the logged-in provider's own catalogue, so the ids can be offered instead of
 * typed from memory (`gpt-5.6-luna` is not a name anyone should have to recall
 * exactly, and a typo fails at the provider rather than here).
 *
 * IT NEVER STOPS BEING FREE TEXT. "Another id…" is always in the list, because
 * the catalogue is a snapshot: a model shipped this morning, an id behind a flag,
 * a provider the list could not be fetched for. Choosing it swaps in the input,
 * and a value the list does not carry selects it automatically.
 *
 * `allowBlank` is for the two slots where empty is a real answer — chat and
 * review both mean "follow the build model" when blank, which is not the same as
 * "unset" and must stay reachable from the list.
 */
export function PiModelField({
  value,
  onChange,
  models,
  placeholder,
  ariaLabel,
  allowBlank = false,
  blankLabel = 'Same as builds',
}: {
  value: string;
  onChange: (next: string) => void;
  /** The provider's catalogue, or null when it could not be read — which is the
   *  plain free-text case, not an error worth showing here. */
  models: PiCatalogueModel[] | null;
  placeholder?: string;
  ariaLabel: string;
  allowBlank?: boolean;
  blankLabel?: string;
}) {
  const listed = Boolean(models?.some((m) => m.id === value));
  // Sticky, so choosing "Another id…" does not snap back to the select the
  // moment the field is empty — the user is mid-type.
  const [custom, setCustom] = useState(false);
  const typing = custom || (Boolean(value) && !listed);

  if (!models || models.length === 0) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        spellCheck={false}
      />
    );
  }

  return (
    <div className="cred">
      <select
        value={typing ? '__custom' : value}
        onChange={(e) => {
          if (e.target.value === '__custom') {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(e.target.value);
        }}
        aria-label={ariaLabel}
      >
        {allowBlank && <option value="">{blankLabel}</option>}
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
            {/* The one property that changes what a build DOES rather than how
                well it does it: a text-only model cannot judge a rendered
                slide. Said on the option, where the choice is made. */}
            {m.vision === false ? ' · text only' : ''}
          </option>
        ))}
        <option value="__custom">Another id…</option>
      </select>
      {typing && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={`${ariaLabel} (id)`}
          spellCheck={false}
        />
      )}
    </div>
  );
}
